import React, { useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import * as powerbi from "powerbi-client";
import reportData from "../config/reports.json";

const slugify = (str) =>
  decodeURIComponent(str)
    .toLowerCase()
    .normalize("NFKD")
    .replace(/&/g, "and")
    .replace(/[^\w\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/--+/g, "-")
    .trim();

const PowerBIEmbed = () => {
  const { dashboard, report, page } = useParams();
  const reportContainerRef = useRef(null);
  const powerbiService = useRef(
    new powerbi.service.Service(
      powerbi.factories.hpmFactory,
      powerbi.factories.wpmpFactory,
      powerbi.factories.routerFactory
    )
  );

  useEffect(() => {
    if (!dashboard || !report || !page) return;

    const matchedPageEntry = reportData.find((entry) => {
      return (
        slugify(entry.dashboard) === slugify(dashboard) &&
        slugify(entry.report) === slugify(report) &&
        entry.pages.some((p) => slugify(p.name) === slugify(page))
      );
    });

    const matchedPage = matchedPageEntry?.pages.find(
      (p) => slugify(p.name) === slugify(page)
    );

    if (!matchedPage) {
      console.error("❌ No matching report section found in reports.json");
      return;
    }

    const { groupId, reportId, sectionId } = matchedPage;

    const embedReport = async () => {
      try {
        const basePath =
          window.location.hostname === "localhost"
            ? "http://localhost:8080/api"
            : "/api";

        const response = await fetch(`${basePath}/powerbi/getEmbedToken`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ groupId, reportId }),
        });

        if (!response.ok) {
          const err = await response.json().catch(() => ({}));
          throw new Error(`Token error: ${err.error || response.statusText}`);
        }

        const data = await response.json();

        if (!data.embedUrl || !data.token) {
          throw new Error("❌ Embed token or URL missing.");
        }

        powerbiService.current.reset(reportContainerRef.current);

        powerbiService.current.embed(reportContainerRef.current, {
          type: "report",
          id: reportId,
          embedUrl: data.embedUrl,
          accessToken: data.token,
          tokenType: powerbi.models.TokenType.Embed,
          pageName: sectionId,
          settings: {
            navContentPaneEnabled: false,
            filterPaneEnabled: false,
          },
        });
      } catch (err) {
        console.error("❌ Failed to embed report:", err.message || err);
      }
    };

    embedReport();
  }, [dashboard, report, page]);

  return <div ref={reportContainerRef} style={{ height: "100vh" }} />;
};

export default PowerBIEmbed;
